import type { HeadingSize } from '@ksyos/design-system/dist/components/heading';
import { useParams } from '@ksyos/router';
import { groupBy } from 'lodash';
import { useEffect, useState } from 'react';
import type { EpisodeCategory } from '@common/enums';
import { ManagerAuthorization } from '@common/enums';
import { isActiveOnDate } from '@common/utils/isActiveOnDate';
import { UrlBuilder } from '@common/utils/urlBuilder';
import { hasOneAuthorization } from '@frontend/utils';
import { useAbortableFetch } from '@frontend/utils/hooks/useAbortableFetch';
import type { IServiceOrganizationSettings } from '../../interfaces/serviceOrganizationSettings';
import type { IServiceOrganizationSettingsByCategory } from '../../interfaces/serviceOrganizationSettingsByCategory';
import { OrganizationActiveCareProvidersView } from './OrganizationActiveCareProvidersView';
import { OrganizationEpisodeCategoriesView } from './OrganizationEpisodeCategoriesView';

export interface OrganizationCarePathsOverviewProps {
    headingSize?: HeadingSize;
    showCareProviders?: boolean;
}

export function OrganizationCarePathsOverview({
    headingSize = 'xlarge',
    showCareProviders = true,
}: OrganizationCarePathsOverviewProps) {
    const abortableFetch = useAbortableFetch();
    const { organizationId } = useParams() as { organizationId: string };

    const [organizationCarePaths, setOrganizationCarePaths] = useState<IServiceOrganizationSettingsByCategory[]>([]);

    useEffect(() => {
        loadCarePaths();
    }, [organizationId]);

    return (
        <>
            <OrganizationEpisodeCategoriesView
                headingSize={headingSize}
                organizationCarePaths={[...organizationCarePaths]}
                organizationId={organizationId}
            />
            {showCareProviders && organizationCarePaths.length > 0 && (
                <OrganizationActiveCareProvidersView organizationId={organizationId} />
            )}
        </>
    );

    async function loadCarePaths() {
        const urlBuilder = new UrlBuilder('/api/serviceOrganizationSettings');
        urlBuilder.add('organizationId', organizationId);
        urlBuilder.add('excludeServicesWithoutServiceGroup', 'true');
        if (hasOneAuthorization(ManagerAuthorization.organizationRead)) {
            urlBuilder.add('isActive', true);
        }
        const url = urlBuilder.build();
        const result = await abortableFetch.fetchJson(url, 'GET');
        const groupedServiceOrganizationSettings = groupServiceOrganizationSettingsByCategory(result.json.items);

        setOrganizationCarePaths([...groupedServiceOrganizationSettings]);
    }

    function groupServiceOrganizationSettingsByCategory(serviceOrganizationSettings: IServiceOrganizationSettings[]) {
        const settingsByCategory = groupBy(
            serviceOrganizationSettings,
            (settings) => settings.service.episodeCategory.name
        );

        return Object.keys(settingsByCategory).map((episodeCategory) => ({
            episodeCategory: episodeCategory as EpisodeCategory,
            activeSettings: settingsByCategory[episodeCategory].filter((setting) =>
                isActiveOnDate(setting, new Date())
            ),
            inactiveSettings: settingsByCategory[episodeCategory].filter(
                (setting) => !isActiveOnDate(setting, new Date())
            ),
        }));
    }
}
